import type { PageHelp } from "../types";

export const homePageHelp: Record<string, PageHelp> = {
  home: {
    title: "Home",
    summary:
      "Your starting point. It shows one card per tool, grouped by area, plus a card for the account you are currently working in.",
    steps: [
      { title: "Read the greeting", body: "It uses your first name and your agency's name. The cards below are what you can use." },
      { title: "Check the account card", body: "The Current account card shows the customer or prospect you are working in. Click it to open that account's overview." },
      { title: "Pick an account if none is selected", body: "If you see No account selected, use the switcher in the top bar. Calls, scripts and calculators need an account to fill in their details." },
      { title: "Scan the areas", body: "Cards are grouped under Home, Sales, Delivery, Insights, Learning and Admin. Each card has an icon, a name and a one-line description." },
      { title: "Open a tool", body: "Click a card to open it. Tools that belong to a single account, such as Live Call, open inside the current account when one is selected." },
    ],
    tips: [
      "A card that seems to be missing is hidden because your role does not have access, or your agency has turned that tool off.",
      "Hover the small dot or badge on a card to see what Hero and Beta mean.",
      "The sidebar has the same tools, so Home is just a friendlier map.",
    ],
    articles: ["learning-home-tour", "learning-wiki-write-edit", "learning-mastery-ranks"],
  },

  help: {
    title: "Help Center",
    summary:
      "Guides, how-tos and a first-hour checklist. Search finds a topic from any words you remember, and every step-by-step guide has a Take me there link.",
    steps: [
      { title: "Start with the checklist", body: "On the overview, tick off Your first hour as you go. Your progress is saved in this browser." },
      { title: "Search for what you need", body: "Type a few words in the search box, such as show rate or webhook. Results show the matching guide and a snippet." },
      { title: "Browse by topic", body: "Use the list on the left to open a topic, then pick a guide. Previous and Next buttons at the bottom move through a topic." },
      { title: "Follow a how-to", body: "In a guide, tick each numbered step as you finish it. Take me there opens the right page in the app." },
      { title: "Share a guide", body: "Use Copy link at the top of a guide to send it to a teammate." },
    ],
    tips: [
      "Press ? on any page to see help for just that page.",
      "The Glossary explains every term and metric in plain language.",
      "Some guides only apply to owners and admins, such as the Settings and automation guides.",
    ],
    articles: ["welcome", "start-tour", "glossary"],
  },

  admin: {
    title: "Data Admin",
    summary:
      "A back-office grid for editing reference tables directly. Core tables (Accounts, MRR snapshots) are read-only here so your revenue and pipeline numbers stay trustworthy.",
    steps: [
      { title: "Choose a table", body: "Click a table button along the top: Accounts, Ad spend, Setter activity, Upcoming items or MRR snapshots. A lock icon means read-only." },
      { title: "Read the rows", body: "The grid shows the newest 500 rows. Hover the (i) on a column header for how to fill it in." },
      { title: "Edit a value", body: "Change a box or dropdown in a row. The Save icon at the end of the row turns active." },
      { title: "Save the row", body: "Click the Save icon. You will see a Saved message and the grid reloads." },
      { title: "Add a row", body: "Fill in the boxes in the bar under the table, then click Add row." },
      { title: "Delete a row", body: "Click the trash icon, then confirm Delete this row?. This cannot be undone." },
    ],
    tips: [
      "Money columns are in cents. Type 150000 for $1,500.",
      "Dates are written year-month-day, such as 2026-03-15.",
      "Changes flow straight into dashboards, so use this for fixes and corrections, not everyday entry.",
      "You only see Data Admin if your role has the Data Admin grid permission. By default that is owners and admins.",
    ],
    articles: ["admin-data-grid", "admin-data-tables"],
  },
};
